/**
 * AttributeList Component
 * 
 * Manages a list of product attributes.
 * Allows adding and removing attributes for product characteristics.
 * Supports individual retention for each attribute.
 * 
 * Requirements: 7.1, 7.3, 7.4
 */

import React, { useState, useEffect } from 'react';
import type { ProductAttribute } from '../types/product';
import { AttributeRow } from './AttributeRow';
import type { WooCommerceAttribute } from './AttributeRow';
import { getProtectedClient } from '../api/client';

export interface AttributeWithRetention extends ProductAttribute {
  retain?: boolean;
}

interface AttributeListProps {
  attributes: AttributeWithRetention[];
  onChange: (attributes: AttributeWithRetention[]) => void;
}

/**
 * Creates an empty attribute entry
 */
export function createEmptyAttribute(): AttributeWithRetention {
  return {
    id: 0,
    name: '',
    value: '',
    visible: true,
    retain: false,
  };
}

/**
 * Add an attribute to the list
 * Returns a new array with the attribute added
 */
export function addAttribute(
  attributes: ProductAttribute[],
  attribute: ProductAttribute
): ProductAttribute[] {
  return [...attributes, attribute];
}

/**
 * Remove an attribute from the list by index
 * Returns a new array with the attribute removed
 */
export function removeAttribute(
  attributes: ProductAttribute[],
  index: number
): ProductAttribute[] {
  if (index < 0 || index >= attributes.length) {
    return attributes;
  }
  return attributes.filter((_, i) => i !== index);
}

export const AttributeList: React.FC<AttributeListProps> = ({
  attributes,
  onChange,
}) => {
  const [availableAttributes, setAvailableAttributes] = useState<WooCommerceAttribute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch available attributes on mount
  useEffect(() => {
    const fetchAttributes = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const client = getProtectedClient();
        const response = await client.get<{ success: boolean; data: WooCommerceAttribute[] }>('/attributes');

        if (response.data.success) {
          setAvailableAttributes(response.data.data);
        } else {
          setError('Failed to load attributes');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load attributes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttributes();
  }, []);

  const handleAddAttribute = () => {
    const newAttribute = createEmptyAttribute();
    onChange(addAttribute(attributes, newAttribute));
  };

  const handleRemoveAttribute = (index: number) => {
    onChange(removeAttribute(attributes, index));
  };

  const handleAttributeChange = (
    index: number,
    field: keyof ProductAttribute,
    value: string | number | boolean
  ) => {
    const newAttributes = attributes.map((attr, i) => {
      if (i === index) {
        return { ...attr, [field]: value };
      }
      return attr;
    });
    onChange(newAttributes);
  };

  const handleRetentionToggle = (index: number) => {
    const newAttributes = attributes.map((attr, i) => {
      if (i === index) {
        return { ...attr, retain: !attr.retain };
      }
      return attr;
    });
    onChange(newAttributes);
  };

  if (isLoading) {
    return (
      <div className="attribute-list attribute-list--loading" data-testid="attribute-list">
        <div className="attribute-header">
          <h3>Product Attributes</h3>
        </div>
        <p className="loading-text">Loading attributes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="attribute-list attribute-list--error" data-testid="attribute-list">
        <div className="attribute-header">
          <h3>Product Attributes</h3>
        </div>
        <p className="error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="attribute-list" data-testid="attribute-list">
      <div className="attribute-header">
        <h3>Product Attributes</h3>
        <button
          type="button"
          className="attribute-add-btn"
          onClick={handleAddAttribute}
          data-testid="attribute-add-btn"
        >
          + Add Attribute
        </button>
      </div>

      {attributes.length === 0 ? (
        <p className="attribute-empty-message">
          No attributes added. Click "Add Attribute" to add one.
        </p>
      ) : (
        <div className="attribute-entries">
          {attributes.map((attribute, index) => (
            <AttributeRow
              key={index}
              attribute={attribute}
              index={index}
              availableAttributes={availableAttributes}
              onChange={handleAttributeChange}
              onRemove={handleRemoveAttribute}
              retain={attribute.retain}
              onRetainToggle={() => handleRetentionToggle(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AttributeList;
